
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Puzzle, Plus, Trash2, Save, RefreshCw, Import } from "lucide-react";
import { db } from "@/integrations/cloudflare/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import CraftGenerator from "@/components/tools/CraftGenerator";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import CopySuppliesList from "@/components/admin/tools/CopySuppliesList";
import CraftIdeasManager from "@/components/admin/tools/CraftIdeasManager";
import CraftIdeasImporter from "@/components/admin/tools/CraftIdeasImporter";
import { useIsMobile } from "@/hooks/use-mobile";

interface CraftSupply {
  id: string;
  name: string;
  checked: boolean;
}

// This is a simplified configuration page - in a real implementation, we would fetch and save the data from/to the database
const CraftGeneratorConfig = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { isLoading: isAuthLoading, isAdmin } = useAdminCheck();
  const [previewTheme, setPreviewTheme] = useState("default");
  const [craftCount, setCraftCount] = useState(0);
  const [activeTab, setActiveTab] = useState("supplies");
  const [showImporter, setShowImporter] = useState(false);
  
  const [defaultSupplies, setDefaultSupplies] = useState<CraftSupply[]>([
    { id: "paper", name: "Colored Paper", checked: true },
    { id: "scissors", name: "Scissors", checked: true },
    { id: "glue", name: "Glue", checked: true },
    { id: "yarn", name: "Yarn", checked: false },
    { id: "buttons", name: "Buttons", checked: false },
    { id: "feltSheets", name: "Felt Sheets", checked: false },
    { id: "pompoms", name: "Pom-poms", checked: false },
    { id: "pipecleaners", name: "Pipe Cleaners", checked: false },
    { id: "googlyeyes", name: "Googly Eyes", checked: false },
    { id: "cottenballs", name: "Cotton Balls", checked: false },
  ]);
  
  const [customSupplyName, setCustomSupplyName] = useState("");

  // Fetch craft count
  useEffect(() => {
    const fetchCraftCount = async () => {
      try {
        const { count, error } = await db
          .from('craft_ideas')
          .select('*', { count: 'exact', head: true });
          
        if (error) throw error;
        setCraftCount(count || 0);
      } catch (error) {
        console.error("Error fetching craft count:", error);
      }
    };
    
    fetchCraftCount();
  }, []);

  const handleToggleDefaultSupply = (id: string) => {
    setDefaultSupplies(defaultSupplies.map(supply => 
      supply.id === id ? { ...supply, checked: !supply.checked } : supply
    ));
  };

  const handleAddCustomSupply = () => {
    if (customSupplyName.trim() === "") return;
    
    const newId = `custom_${Date.now()}`;
    setDefaultSupplies([...defaultSupplies, {
      id: newId,
      name: customSupplyName.trim(),
      checked: true
    }]);
    
    setCustomSupplyName("");
  };

  const handleRemoveSupply = (id: string) => {
    setDefaultSupplies(defaultSupplies.filter(supply => supply.id !== id));
  };

  const handleSaveChanges = () => {
    // In a real implementation, this would save to the database
    toast({
      title: "Changes saved",
      description: "Tool configuration has been updated successfully.",
    });
  };

  const handleResetToDefaults = () => {
    setDefaultSupplies([
      { id: "paper", name: "Colored Paper", checked: true },
      { id: "scissors", name: "Scissors", checked: true },
      { id: "glue", name: "Glue", checked: true },
      { id: "yarn", name: "Yarn", checked: false },
      { id: "buttons", name: "Buttons", checked: false },
      { id: "feltSheets", name: "Felt Sheets", checked: false },
      { id: "pompoms", name: "Pom-poms", checked: false },
      { id: "pipecleaners", name: "Pipe Cleaners", checked: false },
      { id: "googlyeyes", name: "Googly Eyes", checked: false },
      { id: "cottenballs", name: "Cotton Balls", checked: false },
    ]);
    toast({
      title: "Reset complete",
      description: "Tool configuration has been reset to defaults.",
    });
  };

  const handleImportComplete = () => {
    setShowImporter(false);
    setActiveTab("crafts");
    // Refetch craft count
    const fetchCraftCount = async () => {
      try {
        const { count, error } = await db
          .from('craft_ideas')
          .select('*', { count: 'exact', head: true });
          
        if (error) throw error;
        setCraftCount(count || 0);
      } catch (error) {
        console.error("Error fetching craft count:", error);
      }
    };
    fetchCraftCount();
  };

  if (isAuthLoading) {
    return (
      <AdminLayout 
        title="Tool Configuration" 
        backLink="/admin/tools" 
        backLabel="Back to Tools"
      >
        <div className="flex items-center justify-center h-64">
          <p>Loading configuration...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Tool Configuration" 
      backLink="/admin/tools" 
      backLabel="Back to Tools"
    >
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Puzzle className="h-6 w-6 text-elfGreen" />
          Holiday Elf Craft Generator Configuration
        </h1>
        <p className="text-gray-500">
          Configure the craft generator tool that can be embedded in blog posts.
        </p>
      </div>

      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex overflow-x-auto pb-1 -mb-1 scrollbar-none">
          <TabsTrigger value="supplies" className={isMobile ? "text-sm" : ""}>Default Supplies</TabsTrigger>
          <TabsTrigger value="crafts" className={isMobile ? "text-sm" : ""}>
            Craft Ideas {craftCount > 0 && `(${craftCount})`}
          </TabsTrigger>
          <TabsTrigger value="appearance" className={isMobile ? "text-sm" : ""}>Appearance</TabsTrigger>
          <TabsTrigger value="preview" className={isMobile ? "text-sm" : ""}>Preview Tool</TabsTrigger>
        </TabsList>
        
        <TabsContent value="supplies" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between items-center flex-wrap gap-2">
                <span>Configure Default Supplies</span>
                <div className="flex flex-wrap gap-2">
                  <CopySuppliesList supplies={defaultSupplies} />
                  <CopySuppliesList supplies={defaultSupplies} checkedOnly={true} />
                </div>
              </CardTitle>
              <CardDescription>
                Choose which craft supplies will be pre-selected when the tool loads
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {defaultSupplies.map((supply) => (
                  <div key={supply.id} className="flex items-center justify-between space-x-2 border p-3 rounded-md">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id={`supply-${supply.id}`}
                        checked={supply.checked}
                        onCheckedChange={() => handleToggleDefaultSupply(supply.id)}
                      />
                      <Label 
                        htmlFor={`supply-${supply.id}`}
                        className="cursor-pointer"
                      >
                        {supply.name}
                      </Label>
                    </div>
                    {!["paper", "scissors", "glue", "yarn", "buttons", "feltSheets", "pompoms", "pipecleaners", "googlyeyes", "cottenballs"].includes(supply.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSupply(supply.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium mb-2">Add Custom Supply</h3>
                <div className="flex gap-2 flex-wrap">
                  <Input 
                    value={customSupplyName} 
                    onChange={(e) => setCustomSupplyName(e.target.value)}
                    placeholder="Enter new supply name" 
                    className="flex-1 min-w-[200px]"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomSupply();
                      }
                    }}
                  />
                  <Button 
                    onClick={handleAddCustomSupply}
                    disabled={customSupplyName.trim() === ""}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add
                  </Button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between pt-4 border-t flex-wrap gap-3">
              <Button 
                variant="outline" 
                onClick={handleResetToDefaults}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" /> Reset to Defaults
              </Button>
              <Button 
                className="bg-elfGreen hover:bg-elfGreen-dark gap-2"
                onClick={handleSaveChanges}
              >
                <Save className="h-4 w-4" /> Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="crafts" className="space-y-6">
          {showImporter ? (
            <CraftIdeasImporter onImportComplete={handleImportComplete} />
          ) : (
            <>
              <div className="flex justify-end mb-4">
                <Button 
                  className="bg-elfGreen hover:bg-elfGreen-dark gap-2"
                  onClick={() => setShowImporter(true)}
                >
                  <Import className="h-4 w-4" /> Bulk Import Craft Ideas
                </Button>
              </div>
              <CraftIdeasManager 
                selectedSupplies={defaultSupplies.filter(s => s.checked).map(s => s.name)} 
              />
            </>
          )}
        </TabsContent>
        
        <TabsContent value="appearance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Appearance Settings</CardTitle>
              <CardDescription>
                Customize how the craft generator looks when embedded
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="theme-select">Default Theme</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                  <div 
                    className={`border rounded-md p-3 cursor-pointer hover:border-elfGreen transition-colors ${previewTheme === 'default' ? 'border-elfGreen bg-green-50' : ''}`}
                    onClick={() => setPreviewTheme('default')}
                  >
                    <div className="font-medium">Default</div>
                    <div className="text-sm text-gray-600">
                      Standard gray theme that matches any blog design
                    </div>
                  </div>
                  
                  <div 
                    className={`border rounded-md p-3 cursor-pointer hover:border-elfGreen transition-colors ${previewTheme === 'elf-letter-bot' ? 'border-elfGreen bg-green-50' : ''}`}
                    onClick={() => setPreviewTheme('elf-letter-bot')}
                  >
                    <div className="font-medium">Elf Letter Bot</div>
                    <div className="text-sm text-gray-600">
                      Green and red theme that matches the site's branding
                    </div>
                  </div>
                  
                  <div 
                    className={`border rounded-md p-3 cursor-pointer hover:border-elfGreen transition-colors ${previewTheme === 'christmas' ? 'border-elfGreen bg-green-50' : ''}`}
                    onClick={() => setPreviewTheme('christmas')}
                  >
                    <div className="font-medium">Christmas</div>
                    <div className="text-sm text-gray-600">
                      Red and green holiday theme with festive accents
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end pt-4 border-t">
              <Button 
                className="bg-elfGreen hover:bg-elfGreen-dark gap-2"
                onClick={handleSaveChanges}
              >
                <Save className="h-4 w-4" /> Save Changes
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="preview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Tool Preview</CardTitle>
              <CardDescription>
                Preview how the tool will appear with the current configuration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CraftGenerator 
                theme={previewTheme} 
                instanceId="admin-preview" 
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default CraftGeneratorConfig;
